import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { ArrowRight, ShieldCheck } from "lucide-react";

const ROLES = [
  { id: "agent", label: "Agent", desc: "Full roster + financials" },
  { id: "associate", label: "Associate", desc: "Read + case management" },
  { id: "club_rep", label: "Club rep", desc: "Cases only" },
  { id: "tech_director", label: "Tech director", desc: "Position map + scouting" },
  { id: "client", label: "Client", desc: "Read-only own homepage" },
  { id: "admin", label: "Admin", desc: "Billing + user mgmt" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("agent");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ name, email, password, role, invite_code: inviteCode || undefined });
      navigate(role === "agent" || role === "admin" ? "/onboarding" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div
        className="hidden lg:flex flex-col justify-between w-1/2 p-14 text-slate-200 relative"
        style={{ background: "var(--pm-navy)" }}
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(at 30% 30%, rgba(0, 230, 118, 0.18), transparent 50%), radial-gradient(at 80% 80%, rgba(59, 130, 246, 0.12), transparent 50%)",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center font-display font-black text-xl"
                 style={{ background: "var(--pm-green)", color: "#062E17" }}>P</div>
            <div className="font-display text-2xl font-black tracking-tight text-white">Playmaker</div>
          </div>
        </div>
        <div className="relative max-w-md">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-emerald-400 mb-3">
            Create your workspace
          </div>
          <h2 className="font-display text-5xl font-black tracking-tight text-white leading-[1.02]">
            Pick a role. Walk into your roster in 30 seconds.
          </h2>
          <p className="mt-6 text-slate-400 text-base leading-relaxed">
            For testing, you can self-register as any role. In production this would be admin-invited.
          </p>
          <div className="mt-10 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
            <ShieldCheck size={16} className="text-emerald-400" /> Role-based access · Audit log · Encrypted at rest
          </div>
        </div>
        <div className="relative text-xs text-slate-500">v1.0 · © Playmaker FC</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-14 bg-[#F4F6F8]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-lg"
                 style={{ background: "var(--pm-green)", color: "#062E17" }}>P</div>
            <div className="font-display text-2xl font-black tracking-tight">Playmaker</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Get started</div>
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight mt-1">Create your account</h1>
          <p className="text-slate-500 mt-2">
            Already have one?{" "}
            <Link to="/login" className="font-bold text-emerald-700 hover:text-emerald-800" data-testid="link-to-login">
              Sign in
            </Link>
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Full name</label>
              <input
                className="pm-input mt-2"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Marco Rinaldi"
                data-testid="register-name-input"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</label>
              <input
                className="pm-input mt-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@agency.fc"
                data-testid="register-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Password</label>
              <input
                type="password"
                className="pm-input mt-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                data-testid="register-password-input"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Invite code</label>
              <input className="pm-input mt-2 font-mono uppercase" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="PM-XXXXXX (soft launch: invite-only)" data-testid="register-invite-code" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Role</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="role-picker">
                {ROLES.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRole(r.id)}
                    data-testid={`role-option-${r.id}`}
                    className={`text-left rounded-xl px-3 py-2.5 border-2 transition-all duration-200 ${
                      role === r.id
                        ? "border-emerald-500 bg-emerald-50/60"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sm text-slate-900">{r.label}</span>
                      {role === r.id && (
                        <span className="w-4 h-4 rounded-full flex items-center justify-center"
                              style={{ background: "var(--pm-green)" }}>
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5l2 2 4-4" stroke="#062E17" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg"
                   data-testid="register-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="pm-btn-primary w-full h-12 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="register-submit-button"
            >
              {loading ? "Creating account…" : (<>Create account <ArrowRight size={16} /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
